import React from 'react';
import { ApiError } from '@daater/api';
import { Button } from '@daater/ui';
import { useNavigate } from '@tanstack/react-router';
import { useConfirmPaymentFromPayuMutation, useCreatePaymentOrderMutation } from '../api/queries';
import { useAccountStore } from '../lib/account-store';
import { useAuthStore } from '../lib/auth-store';
import {
  buildPayuConfirmationPayload,
  buildPreparedPaymentOrder,
  formatPaymentAmount,
  getPaymentHeader,
  type PreparedPaymentOrder,
} from './payment.utils';

const AUTO_REDIRECT_SECONDS = 6;

const getDisplayAmount = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().replace(/^"+|"+$/g, '');
    const parsed = Number(normalizedValue);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const getStringField = (obj: Record<string, unknown> | undefined, key: string): string => {
  const val = obj?.[key];
  return typeof val === 'string' ? val : '';
};

const getUserName = (
  user: ReturnType<typeof useAuthStore.getState>['user'],
  userData?: Record<string, unknown>
) => {
  const sources = [user as Record<string, unknown> | undefined, userData].filter(Boolean);
  for (const source of sources) {
    const fullName =
      `${getStringField(source, 'firstName')} ${getStringField(source, 'lastName')}`.trim();
    if (fullName) return fullName;
  }
  if (typeof user?.name === 'string' && user.name.trim()) return user.name.trim();
  return 'Usuario';
};

export default function PaymentScreen() {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const account = useAccountStore(state => state.account);
  const createPaymentOrder = useCreatePaymentOrderMutation();
  const confirmPayment = useConfirmPaymentFromPayuMutation();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [confirmMessage, setConfirmMessage] = React.useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [transactionState, setTransactionState] = React.useState<number | null>(null);
  const [redirectCountdown, setRedirectCountdown] = React.useState(AUTO_REDIRECT_SECONDS);
  const [preparedOrder, setPreparedOrder] = React.useState<PreparedPaymentOrder | null>(null);
  const payuFormRef = React.useRef<HTMLFormElement | null>(null);
  const hasSentConfirmation = React.useRef(false);

  const currentSearch = window.location.search;
  const accountUserData = account?.userData;
  const userEmail = user?.email ?? accountUserData?.email ?? '';

  const queryConfirmation = React.useMemo(
    () => buildPayuConfirmationPayload(currentSearch),
    [currentSearch]
  );

  const amount = React.useMemo(() => {
    const queryAmount = new URLSearchParams(currentSearch).get('amount');
    return getDisplayAmount(account?.amount) ?? getDisplayAmount(queryAmount);
  }, [account?.amount, currentSearch]);

  const planName = React.useMemo(() => {
    if (typeof account?.plan?.name === 'string' && account.plan.name.trim()) {
      return account.plan.name;
    }
    if (typeof account?.teamData?.plan?.name === 'string' && account.teamData.plan.name.trim()) {
      return account.teamData.plan.name;
    }
    return '-';
  }, [account?.plan?.name, account?.teamData?.plan?.name]);

  React.useEffect(() => {
    if (!queryConfirmation) {
      setTransactionState(null);
      return;
    }

    setTransactionState(queryConfirmation.transactionState);
    if (hasSentConfirmation.current) return;
    hasSentConfirmation.current = true;

    const sendConfirmation = async () => {
      try {
        await confirmPayment.mutateAsync(queryConfirmation.payload);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          setSessionExpired(true);
          return;
        }
        setConfirmMessage('No se pudo confirmar el estado del pago. Por favor contacta soporte.');
      }
    };

    void sendConfirmation();
  }, [confirmPayment, queryConfirmation]);

  React.useEffect(() => {
    if (!preparedOrder || !payuFormRef.current) return;
    payuFormRef.current.submit();
  }, [preparedOrder]);

  const isApproved = transactionState === 4 && !sessionExpired;

  React.useEffect(() => {
    if (!isApproved) {
      setRedirectCountdown(AUTO_REDIRECT_SECONDS);
      return;
    }

    setRedirectCountdown(AUTO_REDIRECT_SECONDS);

    const timeoutId = window.setTimeout(() => {
      void navigate({ to: '/' });
    }, AUTO_REDIRECT_SECONDS * 1000);

    const intervalId = window.setInterval(() => {
      setRedirectCountdown(prev => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [isApproved, navigate]);

  const handleGoToApp = React.useCallback(() => {
    void navigate({ to: '/' });
  }, [navigate]);

  const handleLogout = React.useCallback(() => {
    clearAuth();
    clearAccount();
    void navigate({ to: '/login' });
  }, [clearAccount, clearAuth, navigate]);

  const handleStartPayment = async () => {
    setErrorMessage(null);
    setConfirmMessage(null);
    setSessionExpired(false);

    if (!amount) {
      setErrorMessage('No encontramos el monto del plan. Contacta soporte para continuar.');
      return;
    }

    try {
      const order = await createPaymentOrder.mutateAsync(amount);
      const builtOrder = buildPreparedPaymentOrder({
        order,
        buyerEmail: userEmail,
        buyerFullName: getUserName(user, accountUserData as Record<string, unknown> | undefined),
      });

      if (!builtOrder.url || !builtOrder.merchantId || !builtOrder.referenceCode) {
        setErrorMessage('La orden de pago está incompleta. Contacta soporte para continuar.');
        return;
      }

      setPreparedOrder(builtOrder);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSessionExpired(true);
        return;
      }
      setErrorMessage('No pudimos crear la orden de pago. Inténtalo nuevamente.');
    }
  };

  const paymentHeader = getPaymentHeader(transactionState);
  const hasTransactionResult = transactionState !== null;
  const isPending = createPaymentOrder.isPending || confirmPayment.isPending;

  return (
    <section className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <div className="rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-charcoal-grey">{paymentHeader.title}</h1>
        <p className="mt-2 text-sm text-slate">{paymentHeader.description}</p>

        {confirmMessage ? (
          <p className="mt-4 rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
            {confirmMessage}
          </p>
        ) : null}

        {sessionExpired ? (
          <div className="mt-4 rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-3 text-sm text-darkish-pink">
            <p>Tu sesión expiró. Ingresa nuevamente para terminar el proceso de pago.</p>
            <Button className="mt-3" onClick={handleLogout}>
              Ir a login
            </Button>
          </div>
        ) : null}

        {!hasTransactionResult ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-pale-grey-three bg-pale-grey p-4">
              <p className="text-sm text-slate">Plan: {planName}</p>
              <p className="mt-1 text-lg font-semibold text-charcoal-grey">
                Valor: ${formatPaymentAmount(amount)}
              </p>
            </div>

            {errorMessage ? (
              <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                {errorMessage}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleLogout} disabled={isPending}>
                Cerrar sesión
              </Button>
              <Button
                onClick={() => void handleStartPayment()}
                disabled={isPending || !amount || sessionExpired}
              >
                {createPaymentOrder.isPending ? 'Procesando...' : 'Pagar'}
              </Button>
            </div>
          </div>
        ) : null}

        {isApproved ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-cool-blue/20 bg-cool-blue/5 px-4 py-4 text-sm text-slate">
              <p>
                Serás redirigido al app en {redirectCountdown}{' '}
                {redirectCountdown === 1 ? 'segundo' : 'segundos'}.
              </p>
            </div>
            <Button onClick={handleGoToApp}>Ir al app</Button>
          </div>
        ) : null}
      </div>

      <form ref={payuFormRef} method="post" action={preparedOrder?.url ?? ''}>
        <input name="tax" type="hidden" value={preparedOrder?.tax ?? 0} />
        <input name="taxReturnBase" type="hidden" value="0" />
        <input name="currency" type="hidden" value="COP" />
        <input name="lng" type="hidden" value="es" />
        <input name="merchantId" type="hidden" value={preparedOrder?.merchantId ?? ''} />
        <input name="accountId" type="hidden" value={String(preparedOrder?.accountId ?? '')} />
        <input name="description" type="hidden" value={preparedOrder?.description ?? ''} />
        <input name="referenceCode" type="hidden" value={preparedOrder?.referenceCode ?? ''} />
        <input name="amount" type="hidden" value={preparedOrder?.total ?? 0} />
        <input name="signature" type="hidden" value={preparedOrder?.signature ?? ''} />
        <input name="buyerFullName" type="hidden" value={preparedOrder?.buyerFullName ?? ''} />
        <input name="buyerEmail" type="hidden" value={preparedOrder?.buyerEmail ?? ''} />
        <input name="responseUrl" type="hidden" value={`${window.location.origin}/payment`} />
      </form>
    </section>
  );
}
